// src/components/UserForm.jsx
import React, { useState, useEffect } from "react";
import Button1 from "@material-ui/core/Button";

const EMPTY = {
  USER_EMAIL:      "",
  USER_FIRSTNAME:  "",
  USER_LASTNAME:   "",
  USER_NICKNAME:   "",
  USER_MOBILE:     "",
  USER_TEL:        "",
  USER_TEL_EXT:    "",
  USER_ROLE_ID:    "",
  USER_ACTIVE:     true,
  EMP_CODE:        "",
  ORIGIN_COUNTRY:  "",
  USER_POSITION:   "",
  BU_LEADER:       false,
  SALES_LEADER:    false,
  SALES_TEAM:      "",
  MS365_OBJECT_ID: "",
};

const inputStyle = (invalid) => ({
  width: "100%", padding: "7px 10px", borderRadius: 6,
  border: `1px solid ${invalid ? "#fca5a5" : "#d1d5db"}`,
  fontSize: 13, color: "#1e293b", fontFamily: "inherit",
  outline: "none", boxSizing: "border-box",
});

const Label = ({ children, required }) => (
  <label style={{ fontWeight: 700, fontSize: 12, color: "#374151", display: "block", marginBottom: 5 }}>
    {children} {required && <span style={{ color: "#dc2626" }}>*</span>}
  </label>
);

const SectionTitle = ({ children }) => (
  <p style={{
    fontSize: 11, fontWeight: 700, color: "#94a3b8",
    textTransform: "uppercase", letterSpacing: "0.05em",
    margin: "20px 0 12px", gridColumn: "1 / -1",
    borderBottom: "1px solid #f1f5f9", paddingBottom: 6,
  }}>
    {children}
  </p>
);

function Toggle({ checked, onChange, label }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}>
      <div
        onClick={() => onChange(!checked)}
        style={{
          position: "relative", width: 36, height: 20, borderRadius: 20, flexShrink: 0,
          background: checked ? "#22c55e" : "#cbd5e1", transition: "background 0.2s", cursor: "pointer",
        }}
      >
        <div style={{
          position: "absolute", top: 3, left: checked ? 17 : 3,
          width: 14, height: 14, borderRadius: "50%",
          background: "#fff", transition: "left 0.2s",
        }} />
      </div>
      <span style={{ fontSize: 13, color: checked ? "#16a34a" : "#94a3b8", fontWeight: 600 }}>
        {label ?? (checked ? "Yes" : "No")}
      </span>
    </label>
  );
}

export function UserForm({ open, user, onSave, onClose }) {
  const [form,   setForm]   = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(user?.CRM_UID);

  useEffect(() => {
    if (!open) return;
    setForm(isEdit ? {
      USER_EMAIL:      user.USER_EMAIL      ?? "",
      USER_FIRSTNAME:  user.USER_FIRSTNAME  ?? "",
      USER_LASTNAME:   user.USER_LASTNAME   ?? "",
      USER_NICKNAME:   user.USER_NICKNAME   ?? "",
      USER_MOBILE:     user.USER_MOBILE     ?? "",
      USER_TEL:        user.USER_TEL        ?? "",
      USER_TEL_EXT:    user.USER_TEL_EXT    ?? "",
      USER_ROLE_ID:    user.USER_ROLE_ID    ?? "",
      USER_ACTIVE:     user.USER_ACTIVE     ?? true,
      EMP_CODE:        user.EMP_CODE        ?? "",
      ORIGIN_COUNTRY:  user.ORIGIN_COUNTRY  ?? "",
      USER_POSITION:   user.USER_POSITION   ?? "",
      BU_LEADER:       user.BU_LEADER       ?? false,
      SALES_LEADER:    user.SALES_LEADER    ?? false,
      SALES_TEAM:      user.SALES_TEAM      ?? "",
      MS365_OBJECT_ID: user.MS365_OBJECT_ID ?? "",
    } : EMPTY);
    setErrors({});
  }, [open, user]);

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const validate = () => {
    const e = {};
    if (!form.USER_EMAIL.trim())     e.USER_EMAIL     = "Required";
    if (!form.USER_FIRSTNAME.trim()) e.USER_FIRSTNAME = "Required";
    if (!form.USER_LASTNAME.trim())  e.USER_LASTNAME  = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await onSave({
        ...form,
        USER_ROLE_ID: form.USER_ROLE_ID !== "" ? Number(form.USER_ROLE_ID) : null,
      });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1300,
      background: "#0006", display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        background: "#fff", borderRadius: 12,
        width: "min(640px, 95vw)", maxHeight: "92vh",
        display: "flex", flexDirection: "column",
        boxShadow: "0 8px 40px #0003",
      }}>
        {/* Header */}
        <div style={{
          padding: "16px 24px", borderBottom: "1px solid #e2e8f0",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          position: "sticky", top: 0, background: "#fff", zIndex: 1,
        }}>
          <span style={{ fontWeight: 800, fontSize: 16, color: "#1e293b" }}>
            {isEdit ? "✏️ Edit User" : "➕ Add User"}
          </span>
          <button onClick={onClose} style={{
            background: "none", border: "none", fontSize: 20,
            cursor: "pointer", color: "#64748b", lineHeight: 1,
          }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", padding: "4px 24px 24px", flex: 1 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>

            {/* ── Personal ── */}
            <SectionTitle>Personal Info</SectionTitle>

            <div>
              <Label required>First name</Label>
              <input style={inputStyle(errors.USER_FIRSTNAME)} value={form.USER_FIRSTNAME}
                onChange={e => set("USER_FIRSTNAME", e.target.value)} placeholder="ชื่อ" />
              {errors.USER_FIRSTNAME && <span style={{ fontSize: 11, color: "#dc2626" }}>{errors.USER_FIRSTNAME}</span>}
            </div>

            <div>
              <Label required>Last name</Label>
              <input style={inputStyle(errors.USER_LASTNAME)} value={form.USER_LASTNAME}
                onChange={e => set("USER_LASTNAME", e.target.value)} placeholder="นามสกุล" />
              {errors.USER_LASTNAME && <span style={{ fontSize: 11, color: "#dc2626" }}>{errors.USER_LASTNAME}</span>}
            </div>

            <div>
              <Label>Nickname</Label>
              <input style={inputStyle(false)} value={form.USER_NICKNAME}
                onChange={e => set("USER_NICKNAME", e.target.value)} placeholder="ชื่อเล่น" />
            </div>

            <div>
              <Label>Emp code</Label>
              <input style={inputStyle(false)} value={form.EMP_CODE}
                onChange={e => set("EMP_CODE", e.target.value)} placeholder="รหัสพนักงาน" />
            </div>

            <div>
              <Label>Position</Label>
              <input style={inputStyle(false)} value={form.USER_POSITION}
                onChange={e => set("USER_POSITION", e.target.value)} placeholder="ตำแหน่ง" />
            </div>

            <div>
              <Label>Country</Label>
              <input style={inputStyle(false)} value={form.ORIGIN_COUNTRY}
                onChange={e => set("ORIGIN_COUNTRY", e.target.value)} placeholder="ประเทศ" />
            </div>

            {/* ── Contact ── */}
            <SectionTitle>Contact</SectionTitle>

            <div style={{ gridColumn: "1 / -1" }}>
              <Label required>Email</Label>
              <input style={inputStyle(errors.USER_EMAIL)} value={form.USER_EMAIL}
                onChange={e => set("USER_EMAIL", e.target.value)}
                placeholder="user@example.com" type="email" />
              {errors.USER_EMAIL && <span style={{ fontSize: 11, color: "#dc2626" }}>{errors.USER_EMAIL}</span>}
            </div>

            <div>
              <Label>Mobile</Label>
              <input style={inputStyle(false)} value={form.USER_MOBILE}
                onChange={e => set("USER_MOBILE", e.target.value)} placeholder="08x-xxx-xxxx" />
            </div>

            <div>
              <Label>Tel</Label>
              <input style={inputStyle(false)} value={form.USER_TEL}
                onChange={e => set("USER_TEL", e.target.value)} placeholder="02-xxx-xxxx" />
            </div>

            <div>
              <Label>Tel ext</Label>
              <input style={inputStyle(false)} value={form.USER_TEL_EXT}
                onChange={e => set("USER_TEL_EXT", e.target.value)} placeholder="ต่อ" />
            </div>

            {/* ── Role & Team ── */}
            <SectionTitle>Role & Team</SectionTitle>

            <div>
              <Label>Role ID</Label>
              <input style={inputStyle(false)} value={form.USER_ROLE_ID} type="number" min="1"
                onChange={e => set("USER_ROLE_ID", e.target.value)} placeholder="1" />
            </div>

            <div>
              <Label>Sales team</Label>
              <input style={inputStyle(false)} value={form.SALES_TEAM}
                onChange={e => set("SALES_TEAM", e.target.value)} placeholder="ชื่อทีม" />
            </div>

            <div style={{ paddingTop: 4 }}>
              <Label>BU Leader</Label>
              <Toggle
                checked={form.BU_LEADER}
                onChange={v => set("BU_LEADER", v)}
              />
            </div>

            <div style={{ paddingTop: 4 }}>
              <Label>Sales Leader</Label>
              <Toggle
                checked={form.SALES_LEADER}
                onChange={v => set("SALES_LEADER", v)}
              />
            </div>

            {/* ── System ── */}
            <SectionTitle>System</SectionTitle>

            <div style={{ gridColumn: "1 / -1" }}>
              <Label>MS365 Object ID</Label>
              <input style={inputStyle(false)} value={form.MS365_OBJECT_ID}
                onChange={e => set("MS365_OBJECT_ID", e.target.value)}
                placeholder="Azure AD Object ID" />
            </div>

            <div style={{ gridColumn: "1 / -1", paddingTop: 4 }}>
              <Label>Status</Label>
              <Toggle
                checked={form.USER_ACTIVE}
                onChange={v => set("USER_ACTIVE", v)}
                label={form.USER_ACTIVE ? "Active" : "Inactive"}
              />
            </div>

          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: "14px 24px", borderTop: "1px solid #e2e8f0",
          display: "flex", justifyContent: "flex-end", gap: 10, background: "#fff",
        }}>
          <Button1 variant="outlined" size="small" onClick={onClose} disabled={saving}
            style={{ textTransform: "none", fontSize: 13 }}>
            ยกเลิก
          </Button1>
          <Button1 variant="contained" color="primary" size="small"
            onClick={handleSave} disabled={saving}
            style={{ textTransform: "none", fontSize: 13, fontWeight: 700 }}>
            {saving ? "⏳ กำลังบันทึก..." : "💾 บันทึก"}
          </Button1>
        </div>
      </div>
    </div>
  );
}
